import React, { PropsWithChildren } from 'react'
import { toast } from '@workwhile/ui'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useDeleteRequirementQuestionMutation } from 'queries/requirements'
import { useRequirementDetailContextValue } from '../../../context/RequirementDetailProvider'
import { useRequirementDetailWorkflowsContextValue } from '../../../context/RequirementDetailWorkflowProvider'

type RemoveQuestionFlowContextState = {
  state: {
    question: NonNullable<
      ReturnType<
        typeof useRequirementDetailWorkflowsContextValue
      >['removeQuestionFlow']['data']
    >['question']
    isPending: boolean
    isError: boolean
    errorMessage: string | undefined
  }
  actions: {
    confirm: () => void
  }
}

const [RemoveQuestionFlowContext, useRemoveQuestionFlowContextValue] =
  createContext<RemoveQuestionFlowContextState>({
    name: 'RemoveQuestionFlowContext',
  })

export const RemoveQuestionFlowProvider = ({ children }: PropsWithChildren) => {
  const {
    state: { id: requirementId },
  } = useRequirementDetailContextValue()

  const {
    removeQuestionFlow: { isOpen, close, data: flowData },
  } = useRequirementDetailWorkflowsContextValue()

  const { mutate, isPending, isError, error } =
    useDeleteRequirementQuestionMutation(requirementId)

  if (isOpen && !flowData) {
    throw new Error(
      'Unable to find remove question flow data. Please report this error to the engineering team.'
    )
  }

  if (!flowData) {
    return null
  }

  const value: RemoveQuestionFlowContextState = {
    state: {
      question: flowData.question,
      isPending,
      isError,
      errorMessage: parseErrorMessage(error),
    },
    actions: {
      confirm: () => {
        mutate(
          { questionId: flowData.question.id },
          {
            onSuccess: () => {
              toast.success('Question removed successfully', {
                position: 'top-center',
              })
              close()
            },
          }
        )
      },
    },
  }

  return (
    <RemoveQuestionFlowContext.Provider value={value}>
      {children}
    </RemoveQuestionFlowContext.Provider>
  )
}

export { useRemoveQuestionFlowContextValue }
